import type { SpaceLayout } from './spaceLayout'
import {
  type AccessGrantInput,
  type AccessGrants,
  type LockReason,
  type RequiredBySection,
  type SpaceLockedDisplay,
  type Visibility
} from './access'

/**
 * Every module a space can have: the keys of the registry
 * (`src/modules/registry.ts`). A module is added there and nowhere else.
 */
export type SpaceModule = keyof typeof import('@/modules/registry').MODULES

export interface Space {
  id: string
  sectionId: string
  communityId: string
  title: string
  description?: string
  slug: string
  icon: string
  order: number
  module: SpaceModule
  externalUrl?: string | null
  visibility: Visibility
  /** Who can create inside the space (APP-50): anyone, staff (owner, admin, moderator) or admins. */
  canCreate?: 'anyone' | 'staff' | 'admins'
  /** Only for whoever administers the community: what this space itself requires. */
  access?: AccessGrants
  /** What the section requires. Combines with `access` by AND, not as more items. */
  requiredBySection?: RequiredBySection | null
  /** Products wired to this space from the products screen, which this modal does not edit. */
  accessFromProducts?: AccessGrants['products']
  /** What whoever has no access sees (F-161a); staff read it to edit. */
  lockedDisplay?: SpaceLockedDisplay
  /** Staff only (F-161a 7b): only the team can ever open it. Computed by the API. */
  adminOnly?: boolean
  /**
   * Only in listings: drawn with a padlock instead of hidden (F-161a). A
   * padlocked space carries only id, title, slug, icon, description, module,
   * sectionId: no `externalUrl`, no `moduleConfig`.
   */
  locked?: boolean
  lockReason?: LockReason
  /**
   * The module's own settings, as the wizard filled them in.
   *
   * It holds the **inner** config, not the map keyed by module: the API stores
   * what `moduleConfig[module]` was on create. `null` on a space created before
   * the column existed — which is most of them today (F-02).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- each module's slice has its own shape
  moduleConfig?: Record<string, any> | null
  /** F-198: how the space lays out its items; `null` or absent is the module's default. */
  layout?: SpaceLayout | null
  /** Feed spaces only (F-162): only its own posts, or every space of its section. */
  feedScope?: 'space' | 'section' | null
  /** Feed spaces only (F-181): the side column's widgets; `null` means the default (events and trending on). */
  feedWidgets?: Partial<FeedWidgets> | null
  createdAt: string
  updatedAt: string
  deletedAt?: string | null
}

/** F-181: the Feed's side column widgets, one switch each. */
export interface FeedWidgets {
  events: boolean
  trending: boolean
  newMembers: boolean
  leaderboard: boolean
}

/** What the API applies when nothing is stored (`feedWidgets: null`). */
export const DEFAULT_FEED_WIDGETS: FeedWidgets = { events: true, trending: true, newMembers: false, leaderboard: false }

export interface CreateSpaceData {
  sectionId: string
  title: string
  description?: string
  slug?: string
  icon?: string
  order: number
  module: SpaceModule
  externalUrl?: string
  visibility: Visibility
  access?: AccessGrantInput
  feedScope?: 'space' | 'section'
  /** F-181: partial merge on the server; `null` goes back to the default. */
  feedWidgets?: Partial<FeedWidgets> | null
  lockedDisplay?: SpaceLockedDisplay
}

export interface UpdateSpaceData {
  sectionId?: string
  title?: string
  description?: string
  slug?: string
  icon?: string
  order?: number
  module?: SpaceModule
  externalUrl?: string
  visibility?: Visibility
  access?: AccessGrantInput
  feedScope?: 'space' | 'section'
  /** F-181: partial merge on the server; `null` goes back to the default. */
  feedWidgets?: Partial<FeedWidgets> | null
  lockedDisplay?: SpaceLockedDisplay
}

export interface SpacesResponse {
  success: boolean
  data: Space[]
  message: string
}

export interface SpaceResponse {
  success: boolean
  data: Space
  message: string
}

export interface SpaceListParams {
  sectionId?: string
  module?: string
  visibility?: Visibility
}